import { useState, type ReactNode } from "react"
import { loadMe, useMeStore, type MeEntitlements } from "@/features/auth/hooks/useCurrentUser"
import { track } from "@/lib/analytics"
import {
  PAUSED_LINE, isPast, metricExhausted, openPaywall, planStateOf, synthesizeOffer,
  type Offer, type QuotaMetric,
} from "@/lib/quotaGate"
import "./gate.css"

/**
 * The one way a Nira action asks "may this run?" (the paywall doc): a paywall sits on the action, before
 * the model runs, and takes nothing back from the screen. `gateAction` pre-checks the allowance in
 * `me.entitlements`; the server's 402 stays the backstop. Three outcomes:
 *   - ok: nothing is used up, run the action.
 *   - a modal opened (line ""): a plan or add-on is on offer, the action does not run.
 *   - a line: nothing to sell (the allowance comes back at midnight) or the reader already said "Not now"
 *     to this paywall this session. The call site shows it quietly next to the action.
 */
export type GateResult = { ok: true } | { ok: false; line: string }

export interface Blocked { offer: Offer; trigger: number | null; line: string; message: string | null }

/** What a used-up allowance says on the page itself, when there is nothing to offer: the count and the reset. */
function inlineLine(e: MeEntitlements, metric: QuotaMetric, offer: Offer, message: string | null): string {
  const m = e.metrics.find((x) => x.metric === metric)
  const state = planStateOf(e)
  if (state === "pro" || state === "grace") {
    const when = m?.window === "month" ? "this month" : "today"
    const resets = m?.window === "month" ? "Resets on the 1st." : "Resets at midnight."
    const count = m && m.limit !== null ? `${m.used} of ${m.limit} used ${when}. ` : ""
    return `${count}${resets}`
  }
  return message ?? (offer === "none" ? "More tomorrow." : "")
}

/** Why the metric is used up, from `me.entitlements` and the doc's offer rules; null while the action may run. */
export function blockedBy(e: MeEntitlements | null | undefined, metric: QuotaMetric, opts: { zeroIfUnlisted?: boolean } = {}): Blocked | null {
  if (!e || e.exempt) return null
  const m = e.metrics.find((x) => x.metric === metric)
  // A paid-only action the server has not listed for this reader counts as none left (a free reader).
  const zero = !m && !!opts.zeroIfUnlisted && !e.subscribed
  if (!zero && !metricExhausted(e, metric)) return null
  const syn = synthesizeOffer(planStateOf(e), metric, m?.window ?? null, zero ? 0 : m?.limit ?? null)
  return { offer: syn.offer, trigger: syn.trigger, line: inlineLine(e, metric, syn.offer, syn.message), message: syn.message }
}

/** True when /me may be stale for the metric (used up, or its window has rolled over): ask again before deciding. */
const suspect = (e: MeEntitlements | null | undefined, metric: QuotaMetric): boolean => {
  const m = e?.metrics.find((x) => x.metric === metric)
  return metricExhausted(e, metric) || (!!m && isPast(m.resets_at))
}

/** Decide on fresh data, open the paywall when there is something to offer. Never throws. */
export async function gateAction(metric: QuotaMetric, opts: { scopeKey?: string; trigger?: number; zeroIfUnlisted?: boolean } = {}): Promise<GateResult> {
  if (suspect(useMeStore.getState().me?.entitlements, metric)) await loadMe(true).catch(() => undefined)
  const e = useMeStore.getState().me?.entitlements
  const blocked = blockedBy(e, metric, opts)
  if (!blocked) return { ok: true }
  if (blocked.offer === "none") return { ok: false, line: blocked.line }
  const trigger = opts.trigger ?? blocked.trigger
  const opened = openPaywall(e, metric, { ...(trigger !== null ? { trigger } : {}), ...(opts.scopeKey ? { scopeKey: opts.scopeKey } : {}) })
  // After "Not now" the same paywall is not shown again this session. An add-on's own sentence still says why ("This
  // month's draft is used."); anything else gets the shared paused line.
  const addon = blocked.offer === "addon_draft" || blocked.offer === "addon_edits"
  return { ok: false, line: opened ? "" : addon && blocked.message ? blocked.message : PAUSED_LINE }
}

/** True for a reader whose plan already includes the paid work (Pro, a Pro in its grace days, staff), or when no plan check runs at all. */
export function useIsPro(): boolean {
  return useMeStore((s) => {
    const e = s.me?.entitlements
    if (!e || e.exempt) return true
    const state = planStateOf(e)
    return state === "pro" || state === "grace"
  })
}

/** The inline line a used-up allowance with nothing to offer shows on the page, live from /me (empty otherwise). */
export function useLimitLine(metric: QuotaMetric): string {
  return useMeStore((s) => {
    const b = blockedBy(s.me?.entitlements, metric)
    return b && b.offer === "none" ? b.line : ""
  })
}

/** A quiet sentence under an action: a limit that resets, or a paywall the reader chose "Not now" on. */
export function GateLine({ text, className = "" }: { text: string; className?: string }) {
  if (!text) return null
  return <p className={`ngl${className ? ` ${className}` : ""}`} role="status">{text}</p>
}

/**
 * A door to a paid action: pressing it (and only pressing it) opens the paywall for `trigger`; once the reader
 * has said "Not now" to that paywall, pressing it again shows a quiet line instead. It gates the action,
 * never anything already on screen.
 */
export function PaywallDoor({ children, metric, trigger, source, className = "npro-door", badge = false }: {
  children: ReactNode
  metric: QuotaMetric
  trigger: number
  /** For the funnel (upgrade_clicked.source). */
  source: string
  className?: string
  /** The "Pro" chip; off where the door already says it is paid. */
  badge?: boolean
}) {
  const [line, setLine] = useState("")
  const press = () => {
    track("upgrade_clicked", { source, metric, plan: "pro", step: "door" })
    const opened = openPaywall(useMeStore.getState().me?.entitlements ?? null, metric, { trigger })
    setLine(opened ? "" : PAUSED_LINE)
  }
  return (
    <>
      <button type="button" className={className} onClick={press}>
        {children}{badge ? <span className="npro-badge sm">Pro</span> : null}
      </button>
      <GateLine text={line} />
    </>
  )
}
